import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import { ButtonGroup, Drawer, Select } from '@reapitform/common-ui'
import { Button } from '@reapit/elements/core/button'
import { FormDetailsFields } from '../../../../components/form-details-fields'
import { FormDetailsValues } from '../../../../components/form-details-fields/schema'
import { useAdminFormsList } from '../../../../api/forms'
import { useCreateAdminFormVersion } from '../../../../api/form-versions'
import { getEnumLabel } from '../../../../api/enums'
import { useAdminEnums } from '../../../../api/enums'

dayjs.extend(customParseFormat)

interface FormOption {
  value: string
  label: string
}

interface CreateVersionDrawerProps {
  isOpen: boolean
  onOpenChange: (open: boolean) => void
}

/**
 * Adds a brand new, blank version to a form that already exists — a genuinely empty canvas, distinct from
 * "Copy version" (which clones an existing version's full content). The user picks the target form from a
 * dropdown, then fills in the version's own fields (label, valid-from, flags). The form entity itself is
 * never touched by this drawer.
 */
export const CreateVersionDrawer: React.FC<CreateVersionDrawerProps> = ({ isOpen, onOpenChange }) => {
  const navigate = useNavigate()
  const formId = 'create-version-drawer'
  const { data: forms } = useAdminFormsList()
  const { data: enums } = useAdminEnums()

  const [selectedFormRef, setSelectedFormRef] = useState<string | null>(null)
  const [selectedFormOption, setSelectedFormOption] = useState<FormOption | null>(null)

  const [createVersion, creating, versionData, versionSuccess] = useCreateAdminFormVersion(selectedFormRef)

  // Deduplicate by referenceID — the list has one row per version, but we only want one entry per form.
  const formOptions = useMemo<FormOption[]>(() => {
    if (!forms) return []
    const seen = new Set<string>()
    const options: FormOption[] = []
    for (const form of forms) {
      if (seen.has(form.referenceID)) continue
      seen.add(form.referenceID)
      const typeLabel = getEnumLabel(enums, 'FormType', form.formType)
      const regionLabel = form.region ? getEnumLabel(enums, 'Region', form.region) : null
      const suffix = regionLabel ? ` — ${typeLabel}, ${regionLabel}` : ` — ${typeLabel}`
      options.push({ value: form.referenceID, label: `${form.name}${suffix}` })
    }
    return options
  }, [forms, enums])

  const handleClose = () => {
    onOpenChange(false)
  }

  // Reset the form selection each time the drawer opens - the success path closes via onOpenChange
  // directly (not handleClose), so relying on close-time cleanup alone leaves the previous selection
  // showing pre-selected on next open.
  useEffect(() => {
    if (isOpen) {
      setSelectedFormRef(null)
      setSelectedFormOption(null)
    }
  }, [isOpen])

  const handleFormSelect = (option: FormOption | null) => {
    setSelectedFormOption(option)
    setSelectedFormRef(option?.value ?? null)
  }

  const handleSubmit = (values: FormDetailsValues) => {
    if (!selectedFormRef) return
    const { version, validFromUTC, ...flags } = values
    const parsedValidFrom = dayjs(validFromUTC, 'DD/MM/YYYY', true)
    createVersion({
      version,
      validFromUTC: parsedValidFrom.isValid() ? parsedValidFrom.toISOString() : validFromUTC,
      ...flags,
    })
  }

  React.useEffect(() => {
    if (!versionSuccess || !versionData?.referenceID || !selectedFormRef) return
    onOpenChange(false)
    navigate(`/forms/${selectedFormRef}/versions/${versionData.referenceID}`)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [versionSuccess, versionData])

  const footer = (
    <ButtonGroup alignment="right">
      <Button size="medium" variant="secondary" disabled={creating} onClick={handleClose} type="button">
        Cancel
      </Button>
      <Button
        size="medium"
        variant="primary"
        isBusy={creating}
        disabled={creating || !selectedFormRef}
        type="submit"
        form={formId}
      >
        Create
      </Button>
    </ButtonGroup>
  )

  return (
    <Drawer isOpen={isOpen} title="Create Version" onClose={handleClose} footerItems={footer}>
      {isOpen && (
        <>
          <Select
            label="Form *"
            placeholder="Select a form"
            options={formOptions}
            value={selectedFormOption}
            getOptionLabel={(o: FormOption) => o.label}
            getOptionValue={(o: FormOption) => o.value}
            onChange={handleFormSelect}
          />
          <FormDetailsFields formId={formId} onSubmit={handleSubmit} hideFormFields />
        </>
      )}
    </Drawer>
  )
}
